/**
 * Fetch-based Supabase REST API Client for Tests and Scripts
 *
 * Replaces `@supabase/supabase-js` createClient() in test utilities and scripts.
 * Talks directly to Supabase PostgREST and Auth Admin APIs via fetch().
 *
 * This is NOT a full replacement for the Supabase client — it covers
 * the subset used by test helpers, E2E utilities, and operational scripts:
 * - PostgREST queries (select, insert, update, delete, upsert)
 * - Auth Admin API (createUser, deleteUser, listUsers, signInWithPassword)
 * - RPC calls
 * - Edge Function invocation
 *
 * Created for NocoDB #2552: Migrate test utilities from @supabase/supabase-js to raw fetch.
 */

// ============================================================================
// Types
// ============================================================================

/** Minimal Supabase-compatible client for tests and scripts */
// eslint-disable-next-line @typescript-eslint/no-unused-vars
export interface SupabaseFetchClient<_Database = unknown> {
  from(table: string): QueryBuilder;
  rpc(fn: string, params?: Record<string, unknown>): RpcBuilder;
  auth: AuthClient;
  functions: FunctionsClient;
}

interface QueryResult<T = any> {
  data: T | null;
  error: PostgrestError | null;
  count?: number | null;
}

interface PostgrestError {
  message: string;
  code: string;
  details?: string;
  hint?: string;
}

interface QueryBuilder {
  select(columns?: string, options?: { count?: 'exact' | 'planned' | 'estimated'; head?: boolean }): FilterBuilder;
  insert(data: Record<string, unknown> | Record<string, unknown>[]): FilterBuilder;
  update(data: Record<string, unknown>): FilterBuilder;
  delete(): FilterBuilder;
  upsert(data: Record<string, unknown> | Record<string, unknown>[], options?: { onConflict?: string; ignoreDuplicates?: boolean }): FilterBuilder;
}

interface FilterBuilder {
  eq(column: string, value: unknown): FilterBuilder;
  neq(column: string, value: unknown): FilterBuilder;
  in(column: string, values: unknown[]): FilterBuilder;
  gte(column: string, value: unknown): FilterBuilder;
  lte(column: string, value: unknown): FilterBuilder;
  match(filter: Record<string, unknown>): FilterBuilder;
  or(filter: string): FilterBuilder;
  order(column: string, options?: { ascending?: boolean }): FilterBuilder;
  limit(count: number): FilterBuilder;
  range(from: number, to: number): FilterBuilder;
  single(): Promise<QueryResult>;
  maybeSingle(): Promise<QueryResult>;
  select(columns?: string, options?: { count?: 'exact' | 'planned' | 'estimated'; head?: boolean }): FilterBuilder;
  then<TResult>(onfulfilled?: (value: QueryResult) => TResult | PromiseLike<TResult>): Promise<TResult>;
}

interface RpcBuilder {
  single(): Promise<QueryResult>;
  then<TResult>(onfulfilled?: (value: QueryResult) => TResult | PromiseLike<TResult>): Promise<TResult>;
}

interface AuthClient {
  admin: AuthAdminClient;
  signInWithPassword(credentials: { email: string; password: string }): Promise<{
    data: { user: any; session: any } | { user: null; session: null };
    error: PostgrestError | null;
  }>;
  getUser(): Promise<{ data: { user: any }; error: PostgrestError | null }>;
  refreshSession(): Promise<{ data: { session: any }; error: PostgrestError | null }>;
}

interface AuthAdminClient {
  createUser(params: {
    email: string;
    password: string;
    email_confirm?: boolean;
    user_metadata?: Record<string, unknown>;
  }): Promise<{ data: { user: any }; error: PostgrestError | null }>;
  deleteUser(userId: string): Promise<{ data: any; error: PostgrestError | null }>;
  listUsers(): Promise<{ data: { users: any[] }; error: PostgrestError | null }>;
  getUserById(userId: string): Promise<{ data: { user: any }; error: PostgrestError | null }>;
}

interface FunctionsClient {
  invoke(name: string, options?: { body?: unknown }): Promise<{ data: any; error: PostgrestError | null }>;
}

// ============================================================================
// Implementation
// ============================================================================

/**
 * Create a fetch-based Supabase client for tests and scripts.
 *
 * Drop-in replacement for:
 *   import { createClient } from '@supabase/supabase-js';
 *   const client = createClient(url, key, options);
 *
 * Replace with:
 *   import { createClient } from '../../lib/test-utils/supabase-fetch';
 *   const client = createClient(url, key, options);
 */
export function createClient<_Database = unknown>(
  supabaseUrl: string,
  supabaseKey: string,
  options?: {
    auth?: { persistSession?: boolean; autoRefreshToken?: boolean };
    global?: { headers?: Record<string, string> };
  }
): SupabaseFetchClient {
  const baseUrl = supabaseUrl.replace(/\/$/, '');
  const globalHeaders = options?.global?.headers || {};

  function makeHeaders(extra?: Record<string, string>): Record<string, string> {
    return {
      apikey: supabaseKey,
      Authorization: globalHeaders.Authorization || `Bearer ${supabaseKey}`,
      'Content-Type': 'application/json',
      ...extra,
    };
  }

  function parsePostgrestResponse(response: Response, body: any): QueryResult {
    if (!response.ok) {
      return {
        data: null,
        error: {
          message: body?.message || body?.error || response.statusText,
          code: body?.code || String(response.status),
          details: body?.details,
          hint: body?.hint,
        },
        count: null,
      };
    }

    const countHeader = response.headers.get('content-range');
    let count: number | null = null;
    if (countHeader) {
      const match = countHeader.match(/\/(\d+|\*)/);
      if (match && match[1] !== '*') {
        count = parseInt(match[1], 10);
      }
    }

    return { data: body, error: null, count };
  }

  function createFilterBuilder(
    table: string,
    method: string,
    body?: unknown,
    selectColumns?: string,
    selectOptions?: { count?: string; head?: boolean },
    upsertOptions?: { onConflict?: string; ignoreDuplicates?: boolean }
  ): FilterBuilder {
    const filters: string[] = [];
    let orderStr = '';
    let limitStr = '';
    let rangeFrom: number | undefined;
    let rangeTo: number | undefined;
    let returnSelect: string | undefined;
    let returnSelectOptions: { count?: string; head?: boolean } | undefined;

    const builder: FilterBuilder = {
      eq(column, value) {
        filters.push(`${column}=eq.${value}`);
        return builder;
      },
      neq(column, value) {
        filters.push(`${column}=neq.${value}`);
        return builder;
      },
      in(column, values) {
        filters.push(`${column}=in.(${values.join(',')})`);
        return builder;
      },
      gte(column, value) {
        filters.push(`${column}=gte.${value}`);
        return builder;
      },
      lte(column, value) {
        filters.push(`${column}=lte.${value}`);
        return builder;
      },
      match(filter) {
        for (const [key, val] of Object.entries(filter)) {
          filters.push(`${key}=eq.${val}`);
        }
        return builder;
      },
      or(filter) {
        filters.push(`or=(${filter})`);
        return builder;
      },
      order(column, options) {
        const dir = options?.ascending === false ? 'desc' : 'asc';
        orderStr = `order=${column}.${dir}`;
        return builder;
      },
      limit(count) {
        limitStr = `limit=${count}`;
        return builder;
      },
      range(from, to) {
        rangeFrom = from;
        rangeTo = to;
        return builder;
      },
      select(columns, options) {
        returnSelect = columns || '*';
        returnSelectOptions = options;
        return builder;
      },
      async single() {
        const result = await execute();
        if (result.error) return result;
        if (Array.isArray(result.data)) {
          if (result.data.length === 0) {
            return {
              data: null,
              error: { message: 'JSON object requested, multiple (or no) rows returned', code: 'PGRST116' },
            };
          }
          return { data: result.data[0], error: null, count: result.count };
        }
        return result;
      },
      async maybeSingle() {
        const result = await execute();
        if (result.error) return result;
        if (Array.isArray(result.data)) {
          if (result.data.length === 0) {
            return { data: null, error: null };
          }
          return { data: result.data[0], error: null, count: result.count };
        }
        return result;
      },
      then(onfulfilled) {
        return execute().then(onfulfilled!);
      },
    };

    async function execute(): Promise<QueryResult> {
      const params = new URLSearchParams();

      // Select columns
      const cols = selectColumns || returnSelect;
      if (cols && cols !== '*') {
        params.set('select', cols);
      } else if (method === 'GET') {
        // Default select all for GET
      }

      // Filters
      for (const f of filters) {
        const [key, ...rest] = f.split('=');
        params.append(key, rest.join('='));
      }

      // Ordering
      if (orderStr) {
        const [, value] = orderStr.split('=');
        params.set('order', value);
      }

      // Limit
      if (limitStr) {
        const [, value] = limitStr.split('=');
        params.set('limit', value);
      }

      // Upsert on_conflict column
      if (upsertOptions?.onConflict) {
        params.set('on_conflict', upsertOptions.onConflict);
      }

      const url = `${baseUrl}/rest/v1/${table}?${params.toString()}`;

      const headers: Record<string, string> = makeHeaders();

      // Prefer header for PostgREST
      const preferParts: string[] = [];

      if (method === 'GET' || method === 'HEAD') {
        // Count preference
        const countMode = selectOptions?.count || returnSelectOptions?.count;
        if (countMode) {
          preferParts.push(`count=${countMode}`);
        }
      } else if (method === 'POST') {
        preferParts.push('return=representation');
        if (upsertOptions) {
          const resolution = upsertOptions.ignoreDuplicates ? 'ignore-duplicates' : 'merge-duplicates';
          preferParts.push(`resolution=${resolution}`);
        }
      } else if (method === 'PATCH') {
        // For updates, return representation if select() was chained
        if (returnSelect) {
          preferParts.push('return=representation');
        } else {
          preferParts.push('return=minimal');
        }
      } else if (method === 'DELETE') {
        if (returnSelect) {
          preferParts.push('return=representation');
        }
        // Count preference for delete
        const countMode = returnSelectOptions?.count;
        if (countMode) {
          preferParts.push(`count=${countMode}`);
        }
      }

      if (preferParts.length > 0) {
        headers.Prefer = preferParts.join(', ');
      }

      // Range header
      if (rangeFrom !== undefined && rangeTo !== undefined) {
        headers.Range = `${rangeFrom}-${rangeTo}`;
      }

      // HEAD request for count-only queries
      const httpMethod = (selectOptions?.head || returnSelectOptions?.head) ? 'HEAD' : method;

      const fetchOptions: RequestInit = {
        method: httpMethod,
        headers,
      };

      // Body for non-GET/HEAD methods
      if (body && method !== 'GET' && method !== 'HEAD') {
        fetchOptions.body = JSON.stringify(body);
      }

      try {
        const response = await fetch(url, fetchOptions);

        // For HEAD requests, return count from header
        if (httpMethod === 'HEAD') {
          const contentRange = response.headers.get('content-range');
          let count: number | null = null;
          if (contentRange) {
            const match = contentRange.match(/\/(\d+)/);
            if (match) {
              count = parseInt(match[1], 10);
            }
          }
          return { data: null, error: null, count };
        }

        const text = await response.text();
        let responseBody: any;
        try {
          responseBody = text ? JSON.parse(text) : null;
        } catch {
          responseBody = text;
        }

        return parsePostgrestResponse(response, responseBody);
      } catch (err) {
        return {
          data: null,
          error: {
            message: err instanceof Error ? err.message : String(err),
            code: 'FETCH_ERROR',
          },
        };
      }
    }

    return builder;
  }

  return {
    from(table: string): QueryBuilder {
      return {
        select(columns = '*', options) {
          return createFilterBuilder(table, 'GET', undefined, columns, options);
        },
        insert(data) {
          return createFilterBuilder(table, 'POST', data);
        },
        update(data) {
          return createFilterBuilder(table, 'PATCH', data);
        },
        delete() {
          return createFilterBuilder(table, 'DELETE');
        },
        upsert(data, options) {
          return createFilterBuilder(table, 'POST', data, undefined, undefined, options);
        },
      };
    },

    rpc(fn: string, params?: Record<string, unknown>): RpcBuilder {
      const execute = async (): Promise<QueryResult> => {
        try {
          const response = await fetch(`${baseUrl}/rest/v1/rpc/${fn}`, {
            method: 'POST',
            headers: makeHeaders(),
            body: JSON.stringify(params || {}),
          });

          const text = await response.text();
          let responseBody: any;
          try {
            responseBody = text ? JSON.parse(text) : null;
          } catch {
            responseBody = text;
          }

          return parsePostgrestResponse(response, responseBody);
        } catch (err) {
          return {
            data: null,
            error: {
              message: err instanceof Error ? err.message : String(err),
              code: 'FETCH_ERROR',
            },
          };
        }
      };

      return {
        async single() {
          const result = await execute();
          if (result.error) return result;
          if (Array.isArray(result.data)) {
            if (result.data.length === 0) {
              return {
                data: null,
                error: { message: 'No rows returned', code: 'PGRST116' },
              };
            }
            return { data: result.data[0], error: null };
          }
          return result;
        },
        then(onfulfilled) {
          return execute().then(onfulfilled!);
        },
      };
    },

    auth: {
      admin: {
        async createUser(params) {
          try {
            const response = await fetch(`${baseUrl}/auth/v1/admin/users`, {
              method: 'POST',
              headers: makeHeaders(),
              body: JSON.stringify({
                email: params.email,
                password: params.password,
                email_confirm: params.email_confirm,
                user_metadata: params.user_metadata,
              }),
            });
            const body = await response.json();
            if (!response.ok) {
              return { data: { user: null }, error: { message: body.message || body.msg || response.statusText, code: String(response.status) } };
            }
            return { data: { user: body }, error: null };
          } catch (err) {
            return { data: { user: null }, error: { message: String(err), code: 'FETCH_ERROR' } };
          }
        },

        async deleteUser(userId) {
          try {
            const response = await fetch(`${baseUrl}/auth/v1/admin/users/${userId}`, {
              method: 'DELETE',
              headers: makeHeaders(),
            });
            if (!response.ok) {
              const body = await response.json().catch(() => ({}));
              return { data: null, error: { message: (body as any).message || response.statusText, code: String(response.status) } };
            }
            return { data: {}, error: null };
          } catch (err) {
            return { data: null, error: { message: String(err), code: 'FETCH_ERROR' } };
          }
        },

        async listUsers() {
          try {
            const response = await fetch(`${baseUrl}/auth/v1/admin/users`, {
              method: 'GET',
              headers: makeHeaders(),
            });
            const body = await response.json();
            if (!response.ok) {
              return { data: { users: [] }, error: { message: body.message || response.statusText, code: String(response.status) } };
            }
            return { data: { users: body.users || [] }, error: null };
          } catch (err) {
            return { data: { users: [] }, error: { message: String(err), code: 'FETCH_ERROR' } };
          }
        },

        async getUserById(userId) {
          try {
            const response = await fetch(`${baseUrl}/auth/v1/admin/users/${userId}`, {
              method: 'GET',
              headers: makeHeaders(),
            });
            const body = await response.json();
            if (!response.ok) {
              return { data: { user: null }, error: { message: body.message || response.statusText, code: String(response.status) } };
            }
            return { data: { user: body }, error: null };
          } catch (err) {
            return { data: { user: null }, error: { message: String(err), code: 'FETCH_ERROR' } };
          }
        },
      },

      async signInWithPassword(credentials) {
        try {
          const response = await fetch(`${baseUrl}/auth/v1/token?grant_type=password`, {
            method: 'POST',
            headers: makeHeaders(),
            body: JSON.stringify({
              email: credentials.email,
              password: credentials.password,
            }),
          });
          const body = await response.json();
          if (!response.ok) {
            return {
              data: { user: null, session: null },
              error: { message: body.message || body.error_description || response.statusText, code: String(response.status) },
            };
          }
          return {
            data: {
              user: body.user,
              session: {
                access_token: body.access_token,
                refresh_token: body.refresh_token,
                expires_in: body.expires_in,
                token_type: body.token_type,
              },
            },
            error: null,
          };
        } catch (err) {
          return {
            data: { user: null, session: null },
            error: { message: String(err), code: 'FETCH_ERROR' },
          };
        }
      },

      async getUser() {
        try {
          const response = await fetch(`${baseUrl}/auth/v1/user`, {
            method: 'GET',
            headers: makeHeaders(),
          });
          const body = await response.json();
          if (!response.ok) {
            return { data: { user: null }, error: { message: body.message || response.statusText, code: String(response.status) } };
          }
          return { data: { user: body }, error: null };
        } catch (err) {
          return { data: { user: null }, error: { message: String(err), code: 'FETCH_ERROR' } };
        }
      },

      async refreshSession() {
        // This client has no internal session state, so the refresh token
        // is unavailable. Throw explicitly rather than sending an empty body
        // and returning a confusing auth error.
        throw new Error('refreshSession: not implemented in supabase-fetch client');
      },
    },

    functions: {
      async invoke(name, options) {
        try {
          const response = await fetch(`${baseUrl}/functions/v1/${name}`, {
            method: 'POST',
            headers: makeHeaders(),
            body: options?.body ? JSON.stringify(options.body) : undefined,
          });
          const text = await response.text();
          let body: any;
          try {
            body = text ? JSON.parse(text) : null;
          } catch {
            body = text;
          }
          if (!response.ok) {
            return { data: null, error: { message: body?.message || body?.error || response.statusText, code: String(response.status) } };
          }
          return { data: body, error: null };
        } catch (err) {
          return { data: null, error: { message: String(err), code: 'FETCH_ERROR' } };
        }
      },
    },
  };
}

/**
 * Type alias for the fetch-based client.
 * Use this wherever `SupabaseClient` was used as a type annotation.
 */
export type { SupabaseFetchClient as SupabaseClient };
